/**
 * PhoneLink — Composant clic-pour-appeler (L13.9 fix Z5).
 *
 * Problème résolu : `<a href="tel:...">` sur desktop déclenche la modale Chrome
 * système "Ouvrir Sélectionner une application ?" qui est interdite par la
 * règle R13 ("pas de modale Chrome, toujours modale interne à l'app").
 *
 * Comportement :
 *   - Mobile (pointer:coarse) : <a href="tel:..."> direct → ouvre dialer natif iOS/Android
 *   - Desktop (pointer:fine) : <button> qui ouvre un Dialog R13 interne avec :
 *       * le numéro affiché en grand
 *       * un bouton "Copier" (navigator.clipboard.writeText)
 *       * un bouton "Fermer"
 *
 * Détection :
 *   Utilise window.matchMedia('(pointer: coarse)') qui est l'API standard
 *   pour distinguer touchscreen vs mouse/trackpad. Ré-évaluée à chaque mount
 *   pour suivre les changements (ex: tablet en mode tactile vs souris connectée).
 *
 * Format affichage :
 *   Le numéro affiché est celui passé tel quel (formaté lisiblement par le parent).
 *   Le lien tel: utilise une version nettoyée (sans espaces ni séparateurs).
 */

import { useState, useEffect, useRef } from 'react';
import { Phone, Copy, Check, X } from 'lucide-react';

interface PhoneLinkProps {
  /** Numéro lisible (ex: "05 46 88 12 34"). */
  number: string;
  /** Classes CSS additionnelles sur le <a>/<button>. */
  className?: string;
  /** Texte affiché (default = number). Permet d'afficher autrement que le numéro brut. */
  children?: React.ReactNode;
}

/** Nettoie un numéro pour le rendre utilisable dans href="tel:" */
function cleanNumber(num: string): string {
  return num.replace(/[\s().-]/g, '');
}

export function PhoneLink({ number, className, children }: PhoneLinkProps) {
  // Détection mobile vs desktop via media query (re-évaluée au mount)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // navigator côté SSR n'a pas matchMedia ; on protège.
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(pointer: coarse)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Sur mobile : <a href="tel:"> direct (comportement natif OK)
  if (isMobile) {
    return (
      <a
        href={`tel:${cleanNumber(number)}`}
        className={
          className ??
          'text-ink hover:text-tech focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-sm'
        }
      >
        {children ?? number}
      </a>
    );
  }

  // Sur desktop : bouton qui ouvre le PhoneDialog interne (pas de modale Chrome)
  return <PhoneButtonWithDialog number={number} className={className}>{children}</PhoneButtonWithDialog>;
}

// ============================================================================
// PhoneButtonWithDialog — desktop : bouton + Dialog R13 interne avec copier
// ============================================================================

function PhoneButtonWithDialog({
  number,
  className,
  children,
}: {
  number: string;
  className?: string;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus trap basique + Escape pour fermer
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    // Focus le bouton "Copier" à l'ouverture
    setTimeout(() => {
      dialogRef.current?.querySelector<HTMLButtonElement>('[data-copy-button]')?.focus();
    }, 50);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback : créer un input temporaire pour la copie legacy
      const ta = document.createElement('textarea');
      ta.value = number;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Échec total : pas grave, le numéro est affiché à l'écran
      }
      document.body.removeChild(ta);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          'text-ink hover:text-tech focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-sm cursor-pointer text-left'
        }
        aria-label={`Afficher le numéro de téléphone ${number}`}
      >
        {children ?? number}
      </button>

      {/* Dialog R13 interne (pas de modale Chrome) */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="phone-dialog-title"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Fermer"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-bg/70 backdrop-blur-sm"
          />

          {/* Card */}
          <div
            ref={dialogRef}
            className="relative z-10 w-full max-w-sm rounded-r-2xl border border-border bg-surface/95 backdrop-blur-glass shadow-glass-hi p-6"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-r-md text-ink-muted hover:bg-surface hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-r-lg bg-tech-tint/70 text-tech-fg"
                aria-hidden="true"
              >
                <Phone className="h-5 w-5" />
              </div>
              <h2 id="phone-dialog-title" className="font-display text-lg font-semibold text-ink">
                Numéro de téléphone
              </h2>
            </div>

            <p className="text-sm text-ink-muted mb-3">
              Pour appeler ce numéro depuis votre ordinateur, copiez-le et utilisez votre application
              de téléphonie (Skype, Teams, mobile…).
            </p>

            {/* Le numéro affiché en grand */}
            <div className="rounded-r-lg border border-border bg-bg-2/60 px-4 py-3 mb-4">
              <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle mb-1">Numéro</div>
              <div className="font-display text-xl font-semibold tabular-nums text-ink select-all">
                {number}
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-r-pill border border-border bg-surface/60 px-4 py-2 text-sm font-medium text-ink-muted backdrop-blur-glass hover:bg-surface/80 hover:text-ink transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                Fermer
              </button>
              <button
                type="button"
                data-copy-button
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 rounded-r-pill bg-tech text-white px-4 py-2 text-sm font-medium hover:bg-tech/90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" aria-hidden="true" />
                    Copié !
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    Copier le numéro
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
